import { useState } from 'react';
import { productDemoStatusLabels } from '../../features/product-management/fixtures/product-submission-demo';
import type { ProviderProduct } from '../../features/product-management/fixtures/provider-data';
import {
  filterProviderProducts,
  findProviderProduct,
  providerProducts,
} from '../../features/product-management/fixtures/provider-data';
import type { ProviderTab } from '../../features/product-management/routes';
import { PROVIDER_BASE, providerTabs } from '../../features/product-management/routes';
import { MyProductSubmissions } from '../../features/product-management/ui/MyProductSubmissions';
import type { ProductSubmissionSession } from '../../features/product-management/ui/ProductSubmissionPrototype';
import {
  ProductSubmissionPrototype,
  useProductSubmissionSession,
} from '../../features/product-management/ui/ProductSubmissionPrototype';
import {
  SynLiveMySubmissions,
  SynLiveProductManagement,
} from '../../features/product-management/ui/SynLiveProductManagement';
import { syntheticDemoPortalsEnabled } from '../../shared/config/api-origin';
import { PortalIcon, PortalShell } from '../../shared/platform/PortalShell';
import {
  SynLiveProductReviewProvider,
  SynLiveSessionPanel,
  synLiveEnabled,
} from '../../shared/product-review/session/ProductReviewSession';
import '../../shared/product-review/ui/product-review.css';
import { VendorWorkbench } from './VendorWorkbench';

function ProductImage({
  product,
  className = '',
}: {
  product: ProviderProduct;
  className?: string;
}) {
  return product.image ? (
    <img
      className={className}
      src={product.image}
      alt={`${product.name} ${product.model} — 设计包演示素材`}
    />
  ) : (
    <div className={`provider-image-placeholder ${className}`}>
      <PortalIcon name="box" />
      <span>{product.category}</span>
      <small>图片待补 · DEMO</small>
    </div>
  );
}
function Status({ status }: { status: string }) {
  return (
    <span
      className={`provider-status ${status === '发布展示' ? 'provider-status-green' : status === '审核中' ? 'provider-status-blue' : 'provider-status-gray'}`}
    >
      {status} · 演示
    </span>
  );
}
function SectionHead({
  title,
  href,
  action = '查看全部',
}: {
  title: string;
  href?: string;
  action?: string;
}) {
  return (
    <div className="provider-section-head">
      <h2>{title}</h2>
      {href && <a href={href}>{action} ›</a>}
    </div>
  );
}
function Home() {
  const recent = [providerProducts[0]!, providerProducts[1]!, providerProducts[2]!];
  return (
    <div className="provider-home portal-card">
      <div className="provider-welcome">
        <h1>你好，张工</h1>
        <p>欢迎使用 AI消防+救援 技术提供方门户</p>
        <span className="provider-demo-label">本地交互原型 · DEMO_ONLY · 非正式审核</span>
      </div>
      <section className="provider-hero" aria-label="消防装备产品示意横幅">
        <img src="/portals/hero.png" alt="设计包红色消防救援机器人示意，非现场证据" />
        <div className="provider-hero-shade" />
        <div className="provider-hero-copy">
          <h2>
            创新技术
            <br />
            助力更安全的世界
          </h2>
          <p>专业的消防装备与解决方案提供商</p>
        </div>
        <div className="provider-hero-english">
          TECHNOLOGY
          <br />
          FOR A SAFER WORLD
        </div>
        <div className="provider-hero-values">
          <span>◇ 更智能的装备</span>
          <span>♧ 更高效的协同</span>
          <span>♡ 更安全的社会</span>
        </div>
      </section>
      <div className="provider-stats">
        {(
          [
            {
              label: '我的产品',
              count: 12,
              subtitle: '已创建产品 · 示例统计',
              icon: 'box',
              tone: 'blue',
              href: '/products',
            },
            {
              label: '待完善资料',
              count: 3,
              subtitle: '需要补充资料 · 示例统计',
              icon: 'file',
              tone: 'green',
              href: '/documents',
            },
            {
              label: '提交审核中',
              count: 4,
              subtitle: '等待审核结果 · 示例统计',
              icon: 'share',
              tone: 'orange',
              href: '/submissions',
            },
            {
              label: '发布展示产品',
              count: 8,
              subtitle: '演示展示 · 非真实发布',
              icon: 'box',
              tone: 'purple',
              href: '/products',
            },
          ] as const
        ).map((stat) => (
          <a
            className={`provider-stat provider-stat-${stat.tone}`}
            key={stat.label}
            href={`${PROVIDER_BASE}${stat.href}?synLive=0`}
          >
            <span className="provider-stat-icon">
              <PortalIcon name={stat.icon} />
            </span>
            <div>
              <span>{stat.label}</span>
              <strong>
                {stat.count}
                <small>个</small>
              </strong>
              <p>{stat.subtitle}</p>
            </div>
            <span className="provider-stat-arrow">›</span>
          </a>
        ))}
      </div>
      <div className="provider-home-bottom">
        <section className="provider-recent portal-card">
          <SectionHead title="最近编辑的产品" href={`${PROVIDER_BASE}/products?synLive=0`} />
          <div className="provider-recent-list">
            {recent.map((product) => (
              <a
                key={product.id}
                className="provider-recent-row"
                href={`${PROVIDER_BASE}/products/${product.id}?synLive=0`}
              >
                <ProductImage product={product} />
                <div className="provider-recent-name">
                  <strong>{product.name}</strong>
                  <span>型号：{product.model}</span>
                </div>
                <time>更新时间：{product.updated}</time>
                <Status status={product.status} />
                <span className="provider-row-more" aria-hidden="true">
                  ›
                </span>
              </a>
            ))}
          </div>
        </section>
        <section className="provider-reminders portal-card">
          <SectionHead title="待办提醒" href={`${PROVIDER_BASE}/submissions?synLive=0`} />
          {[
            {
              icon: '!',
              name: '产品《FR-300》资料不完整',
              text: '请补充技术参数和说明书',
              date: '示例',
              id: 'fr-300',
              tone: 'red',
            },
            {
              icon: '◷',
              name: '产品《HM-200》正在审核中',
              text: '演示队列记录 · 非真实审核',
              date: '12-08',
              id: 'hm-200',
              tone: 'blue',
            },
            {
              icon: '!',
              name: '演示反馈：《XT-100》需修改',
              text: '查看样例意见及资料补充提示',
              date: '12-08',
              id: 'xt-100',
              tone: 'red',
            },
          ].map((item) => (
            <a
              key={item.id}
              className="provider-reminder"
              href={`${PROVIDER_BASE}/products/${item.id}?synLive=0`}
            >
              <span className={`provider-reminder-icon provider-reminder-${item.tone}`}>
                {item.icon}
              </span>
              <div>
                <strong>{item.name}</strong>
                <p>{item.text}</p>
              </div>
              <small>{item.date}</small>
            </a>
          ))}
        </section>
      </div>
    </div>
  );
}
function ProductTabContent({ product, tab }: { product: ProviderProduct; tab: ProviderTab }) {
  if (tab === '技术参数')
    return (
      <div className="provider-tab-content">
        <h3>技术参数 · 厂家资料示例</h3>
        <p className="portal-muted">参数尚未核实，不构成能力认证或实际可用性结论。</p>
        <dl className="provider-parameters">
          <div>
            <dt>产品型号</dt>
            <dd>{product.model}</dd>
          </div>
          <div>
            <dt>资料版本</dt>
            <dd>v1.0 · DEMO</dd>
          </div>
          <div>
            <dt>工作温度 / 防护等级</dt>
            <dd>未知 · 待补技术文件</dd>
          </div>
          <div>
            <dt>续航 / 操控距离</dt>
            <dd>未知 · 待厂家补充</dd>
          </div>
        </dl>
      </div>
    );
  if (tab === '功能与技术')
    return (
      <div className="provider-tab-content">
        <h3>功能与技术</h3>
        <p>{product.features}</p>
        <div className="provider-advantage">
          <h3>技术资料结构</h3>
          <ul>
            <li>环境感知与信息采集说明</li>
            <li>操控接口与通信设计说明</li>
            <li>风险提示及使用限制</li>
          </ul>
        </div>
        <p className="portal-muted">以上为厂家描述样例，不代表已验证的现场能力。</p>
      </div>
    );
  if (tab === '图片与资料')
    return (
      <div className="provider-tab-content">
        <h3>图片与资料</h3>
        <div className="provider-document-row">
          <span>产品演示图</span>
          <span>{product.image ? '设计包素材 · 非官方设备证据' : '未知 · 暂未提供'}</span>
        </div>
        <div className="provider-document-row">
          <span>产品说明书.pdf</span>
          <span>资料待补 · 未连接文件服务</span>
        </div>
        <div className="provider-document-row">
          <span>技术检测报告</span>
          <span>未知 · 未核实认证</span>
        </div>
        <button className="portal-button" disabled>
          上传资料 · 后续接入
        </button>
      </div>
    );
  if (tab === '版本与反馈')
    return (
      <div className="provider-tab-content">
        <h3>版本与反馈 · 演示记录</h3>
        <div className="provider-version">
          <strong>v1.0</strong>
          <span>2024-12-01 · 当前演示资料</span>
          <p>完善产品用途描述与图片分类。样例展示状态不等于 Owner 已发布。</p>
        </div>
        <div className="provider-version">
          <strong>v0.9</strong>
          <span>2024-11-28 · 资料草稿示例</span>
          <p>反馈：补充技术参数来源，明确应用场景为厂家用途声明。</p>
        </div>
        <a href={`${PROVIDER_BASE}/submissions?synLive=0`}>查看提交与审核记录 ›</a>
      </div>
    );
  return (
    <div className="provider-overview provider-tab-content">
      <section>
        <h3>产品简介</h3>
        <p>{product.description}</p>
        <div className="provider-advantage">
          <h3>产品特点 · 厂家声明</h3>
          <ul>
            <li>面向复杂环境的产品设计</li>
            <li>多传感器信息采集与回传设计</li>
            <li>模块化结构与部署说明</li>
            <li>远程操控设计与人员风险提示</li>
            <li>技术能力、认证及实测结果待核实</li>
          </ul>
        </div>
      </section>
      <section className="provider-applications">
        <SectionHead
          title="应用示例"
          href={`${PROVIDER_BASE}/documents?synLive=0`}
          action="查看资料"
        />
        <figure>
          <img src="/portals/hero.png" alt="设计包机器人用途示意，不代表真实任务" />
          <figcaption>建筑火灾中的侦察用途声明 · 演示</figcaption>
        </figure>
        <figure>
          <img src="/portals/banner.png" alt="设计包消防装备场景示意，非现场证据" />
          <figcaption>环境监测用途示意 · 未验证场景适用性</figcaption>
        </figure>
      </section>
    </div>
  );
}
function Products({ id, session }: { id: string | undefined; session: ProductSubmissionSession }) {
  const { query, status, category } = session.filters;
  const setQuery = (value: string) =>
    session.setFilters((previous) => ({ ...previous, query: value }));
  const setStatus = (value: string) =>
    session.setFilters((previous) => ({ ...previous, status: value }));
  const setCategory = (value: string) =>
    session.setFilters((previous) => ({ ...previous, category: value }));
  const [tab, setTab] = useState<ProviderTab>('产品概览');
  const [imageView, setImageView] = useState<'product' | 'context'>('product');
  const localRecord = session.records.find((record) => record.demoProductDraftId === id);
  const product = id
    ? (findProviderProduct(id) ?? (localRecord ? providerProducts[0] : undefined))
    : providerProducts[0];
  const filtered = filterProviderProducts(query, status, category);
  const filteredDrafts = session.records.filter(
    (record) =>
      `${record.fields.name} ${record.fields.model} ${record.fields.category}`
        .toLowerCase()
        .includes(query.trim().toLowerCase()) &&
      (category === '全部品类' || record.fields.category === category) &&
      (status === '全部' ||
        (status === '草稿' && record.status === 'DRAFT') ||
        (status === '审核中' && record.status === 'PENDING_REVIEW') ||
        (status === '退回修改' && record.status === 'CHANGES_REQUESTED')),
  );
  if (!product)
    return (
      <section className="portal-card provider-empty">
        <h1>产品资料不存在</h1>
        <p>该 DEMO 产品编号没有对应资料，信息保持未知。</p>
        <a href={`${PROVIDER_BASE}/products?synLive=0`}>返回我的产品</a>
      </section>
    );
  return (
    <>
      <nav className="provider-breadcrumb" aria-label="面包屑">
        <a href={`${PROVIDER_BASE}?synLive=0`}>⌂ 工作台</a>
        <span>/</span>
        <a href={`${PROVIDER_BASE}/products?synLive=0`}>我的产品</a>
        <span>/</span>
        <strong>
          {localRecord?.fields.name ?? product.name} {localRecord?.fields.model ?? product.model}
        </strong>
      </nav>
      <div className="provider-products-layout">
        <aside className="provider-product-list portal-card" aria-label="我的产品列表">
          <div className="provider-list-heading">
            <h2>
              我的产品 <small>（{providerProducts.length} 个演示样例）</small>
            </h2>
            <button
              className="portal-button"
              disabled={!syntheticDemoPortalsEnabled()}
              onClick={session.openNew}
            >
              ＋ 新增产品（演示）
            </button>
          </div>
          <label className="provider-search">
            <span aria-hidden="true">⌕</span>
            <input
              type="search"
              aria-label="搜索产品名称、型号或关键词"
              placeholder="搜索产品名称、型号或关键词…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <label className="provider-category">
            产品类别
            <select
              aria-label="筛选产品类别"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              {['全部品类', '侦察与监测设备', '灭火装备', '无人机', '个人防护'].map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
          <div className="provider-status-filters" aria-label="产品状态筛选">
            {['全部', '发布展示', '审核中', '草稿', '退回修改'].map((value) => (
              <button
                key={value}
                aria-pressed={status === value}
                className={status === value ? 'is-active' : ''}
                onClick={() => setStatus(value)}
              >
                {value}
              </button>
            ))}
          </div>
          <div className="provider-products-scroll">
            {filteredDrafts.map((record) => (
              <a
                className={`provider-list-product provider-local-draft ${record.demoProductDraftId === id ? 'is-selected' : ''}`}
                key={record.demoProductDraftId}
                href={`${PROVIDER_BASE}/products/${record.demoProductDraftId}?synLive=0`}
                aria-current={record.demoProductDraftId === id ? 'page' : undefined}
              >
                <div className="provider-image-placeholder">
                  <PortalIcon name="file" />
                  <small>本地草稿</small>
                </div>
                <div>
                  <strong>{record.fields.name}</strong>
                  <span>
                    {record.fields.model || 'UNKNOWN'} · {record.version}
                  </span>
                  <span>{productDemoStatusLabels[record.status]}</span>
                  <span>
                    {record.origin === 'SESSION' ? '本次会话 · 刷新清空' : '独立静态反馈样例'}
                  </span>
                </div>
              </a>
            ))}
            {filtered.map((item) => (
              <a
                className={`provider-list-product ${item.id === product.id ? 'is-selected' : ''}`}
                key={item.id}
                href={`${PROVIDER_BASE}/products/${item.id}?synLive=0`}
                aria-current={item.id === product.id ? 'page' : undefined}
              >
                <ProductImage product={item} />
                <div>
                  <strong>
                    {item.name} {item.model}
                  </strong>
                  <span>型号：{item.model}</span>
                  <span>更新：{item.updated}</span>
                  <Status status={item.status} />
                </div>
              </a>
            ))}
            {filtered.length === 0 && filteredDrafts.length === 0 && (
              <div className="provider-empty">
                <h3>暂无匹配产品</h3>
                <p>调整搜索或筛选条件后重试。</p>
                <button
                  className="portal-button"
                  onClick={() => {
                    setQuery('');
                    setStatus('全部');
                    setCategory('全部品类');
                  }}
                >
                  清除筛选
                </button>
              </div>
            )}
          </div>
          <p className="provider-fixture-note">独立本地 DEMO 产品集合 · 不连接 05 资产</p>
        </aside>
        {localRecord ? (
          <article className="provider-product-detail portal-card provider-local-record">
            <MyProductSubmissions session={session} selected={localRecord} />
            <a href={`${PROVIDER_BASE}/products?synLive=0`}>返回 W1 产品列表 ›</a>
          </article>
        ) : (
          <article className="provider-product-detail portal-card">
            <div className="provider-detail-top">
              <div className="provider-gallery">
                {imageView === 'context' ? (
                  <img
                    className="provider-main-image"
                    src="/portals/hero.png"
                    alt="厂家用途演示示意，非设备运行证据"
                  />
                ) : (
                  <ProductImage product={product} className="provider-main-image" />
                )}
                <div className="provider-thumbnails">
                  <button
                    aria-label="查看产品演示图"
                    aria-pressed={imageView === 'product'}
                    onClick={() => setImageView('product')}
                  >
                    <ProductImage product={product} />
                  </button>
                  <button
                    aria-label="查看用途演示图"
                    aria-pressed={imageView === 'context'}
                    onClick={() => setImageView('context')}
                  >
                    <img src="/portals/hero.png" alt="用途示意" />
                  </button>
                  <span>
                    设计包示意素材
                    <br />
                    非现场证据
                  </span>
                </div>
              </div>
              <div className="provider-summary">
                <div className="provider-detail-title">
                  <h1>
                    {product.name} {product.model}
                  </h1>
                  <button
                    className="portal-button"
                    disabled={!syntheticDemoPortalsEnabled()}
                    onClick={() => session.openFixture(product)}
                  >
                    编辑拟议 v2（演示）
                  </button>
                </div>
                <div className="provider-meta">
                  <Status status={product.status} />
                  <span>版本 v1.0</span>
                  <span>资料日期 {product.updated}</span>
                </div>
                <dl className="provider-info-table">
                  {[
                    ['产品类别', product.category],
                    ['品牌', 'XX科技 · DEMO 企业'],
                    ['型号', product.model],
                    ['主要用途', product.purpose],
                    ['核心特点', product.features],
                    ['适用场景', product.scenes],
                  ].map(([key, value]) => (
                    <div key={key}>
                      <dt>{key}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="provider-tags">
                  {['厂家用途声明', '技术资料示例', '未核实能力', 'DEMO_ONLY'].map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="provider-tabs" role="tablist" aria-label="产品详情">
              {providerTabs.map((value, index) => (
                <button
                  key={value}
                  role="tab"
                  id={`provider-tab-${index}`}
                  aria-controls="provider-tab-panel"
                  aria-selected={tab === value}
                  onClick={() => setTab(value)}
                >
                  {value}
                </button>
              ))}
            </div>
            <div
              role="tabpanel"
              id="provider-tab-panel"
              aria-labelledby={`provider-tab-${providerTabs.indexOf(tab)}`}
            >
              <ProductTabContent product={product} tab={tab} />
            </div>
          </article>
        )}
      </div>
    </>
  );
}
function Documents({ session }: { session: ProductSubmissionSession }) {
  return (
    <section className="portal-card provider-skeleton">
      <SectionHead title="产品资料与文档" />
      <p className="provider-skeleton-intro">
        按产品查看图片、说明书及技术文件。所有资料均为独立 DEV 样例。
      </p>
      <div className="provider-document-grid">
        {session.records.map((record) => (
          <section className="portal-card" key={record.demoProductDraftId}>
            <h3>{record.fields.name}</h3>
            <p className="product-demo-help">
              {record.fields.model || 'UNKNOWN'} · {record.version} · 本端 DEMO
            </p>
            <div className="provider-document-row">
              <span>资料来源</span>
              <span>{record.fields.source || '未知 · 待补'}</span>
            </div>
            {record.fields.files.length ? (
              record.fields.files.map((file, index) => (
                <div className="provider-document-row" key={`${file.name}-${index}`}>
                  <span>{file.type.startsWith('image/') ? '产品图片' : '产品文档'}</span>
                  <span>{file.name} · 待上传</span>
                </div>
              ))
            ) : (
              <div className="provider-document-row">
                <span>图片 / 手册 / 证明文件</span>
                <span>待上传 · 无正式存储</span>
              </div>
            )}
            <a href={`${PROVIDER_BASE}/products/${record.demoProductDraftId}?synLive=0`}>
              查看本地资料 ›
            </a>
          </section>
        ))}
        {providerProducts.slice(0, 3).map((product) => (
          <section className="portal-card" key={product.id}>
            <div className="provider-document-product">
              <ProductImage product={product} />
              <div>
                <h3>{product.name}</h3>
                <p>{product.model} · 厂家资料</p>
              </div>
            </div>
            <div className="provider-document-row">
              <span>产品图片</span>
              <span>{product.image ? '设计包演示图' : '待补 · 未知'}</span>
            </div>
            <div className="provider-document-row">
              <span>产品说明书</span>
              <span>待补资料</span>
            </div>
            <div className="provider-document-row">
              <span>技术报告</span>
              <span>未提供 · 未核实</span>
            </div>
            <a href={`${PROVIDER_BASE}/products/${product.id}?synLive=0`}>查看产品资料 ›</a>
          </section>
        ))}
      </div>
      <button className="portal-button" disabled>
        上传文件 · 后续接入
      </button>
      <p className="portal-muted">本轮不接收文件，不进行真实上传或保存。</p>
    </section>
  );
}
function Submissions({ session }: { session: ProductSubmissionSession }) {
  return (
    <section className="portal-card provider-skeleton">
      <SectionHead title="提交与审核" />
      <p className="provider-skeleton-intro">
        查看样例资料版本和演示反馈，区分本地草稿、样例审核队列与发布展示。
      </p>
      <MyProductSubmissions session={session} />
      <div className="provider-submission-list">
        {providerProducts.slice(0, 5).map((product) => (
          <article key={product.id} className="provider-submission">
            <div>
              <h3>
                {product.name} {product.model}
              </h3>
              <p>资料版本 v1.0 · 日期 {product.updated} · DEMO_ONLY</p>
            </div>
            <Status status={product.status} />
            <a href={`${PROVIDER_BASE}/products/${product.id}?synLive=0`}>查看详情 ›</a>
          </article>
        ))}
      </div>
      <p className="provider-boundary-note">
        示例审核记录不是正式跨端提交或管理员审批。08 演示意见不代表 06 Owner
        发布，不连接真实管理写入。
      </p>
      <button className="portal-button" disabled>
        正式提交审核 · 后续接入
      </button>
    </section>
  );
}
function Organization() {
  return (
    <section className="portal-card provider-skeleton">
      <SectionHead title="企业资料" />
      <div className="provider-organization-head">
        <span className="provider-organization-avatar">XX</span>
        <div>
          <h1>XX科技有限公司</h1>
          <p>技术提供方 · DEMO 企业样例</p>
          <span className="provider-demo-label">企业身份与资质未知 · 未核实</span>
        </div>
      </div>
      <dl className="provider-organization-data">
        <div>
          <dt>品牌名称</dt>
          <dd>XX科技 · 示例品牌</dd>
        </div>
        <div>
          <dt>业务说明</dt>
          <dd>消防装备与技术资料展示示例</dd>
        </div>
        <div>
          <dt>联系人</dt>
          <dd>张工 · DEMO</dd>
        </div>
        <div>
          <dt>联系电话 / 邮箱</dt>
          <dd>未知 · 尚未提供真实联系方式</dd>
        </div>
        <div>
          <dt>主体资质文件</dt>
          <dd>未知 · 未提供、未核实</dd>
        </div>
      </dl>
      <button className="portal-button" disabled>
        编辑企业资料 · 后续接入
      </button>
      <p className="provider-boundary-note">此页不创建真实企业身份、账号或机构授权。</p>
    </section>
  );
}
export function ProviderPortal({ path }: { path?: string }) {
  const session = useProductSubmissionSession();
  const currentPath =
    path ?? (typeof window === 'undefined' ? PROVIDER_BASE : window.location.pathname);
  const suffix = currentPath.slice(PROVIDER_BASE.length).replace(/\/$/, '');
  const isProducts = suffix === '/products' || suffix.startsWith('/products/');
  const title = isProducts
    ? '06 厂商端 — 产品管理与详情'
    : suffix === '/documents'
      ? '06 厂商端 — 产品资料与文档'
      : suffix === '/submissions'
        ? '06 厂商端 — 提交与审核'
        : suffix === '/organization'
          ? '06 厂商端 — 企业资料'
          : '06 厂商端 — 工作台（首页）';
  if (synLiveEnabled()) {
    // The confirmed portal is NOT replaced. Its shell, navigation and page layout stay; only the
    // business facts come from the durable 06 reads instead of the demo fixtures.
    const liveId = isProducts ? suffix.slice('/products/'.length) || undefined : undefined;
    return (
      <PortalShell
        portal="provider"
        title={
          isProducts
            ? '06 厂商端 — 产品管理 LIVE'
            : suffix === '/submissions'
              ? '06 厂商端 — 提交与审核 LIVE'
              : title
        }
        mode="SYN_LIVE"
      >
        <div className="provider-portal">
          <SynLiveProductReviewProvider role="supplier">
            <SynLiveSessionPanel />
            {suffix === '' ? (
              <VendorWorkbench />
            ) : isProducts ? (
              <SynLiveProductManagement id={liveId} />
            ) : suffix === '/submissions' ? (
              <SynLiveMySubmissions />
            ) : suffix === '/documents' ? (
              <>
                <section className="portal-card">
                  <h2>产品资料与文档</h2>
                  <p>
                    在“资料与附件档案”中读取产品后，可编辑结构化资料并上传、查看不可变附件。其他工作区未启用的资料能力会明确显示不可用。
                  </p>
                </section>
                <SynLiveProductManagement />
              </>
            ) : (
              <section className="portal-card provider-skeleton">
                <h1>{suffix === '/organization' ? '企业资料' : '页面未接入'}</h1>
                <p>当前后端尚未实现企业档案管理接口；不会把演示企业资料作为真实账号资料显示。</p>
              </section>
            )}
          </SynLiveProductReviewProvider>
        </div>
      </PortalShell>
    );
  }
  return (
    <PortalShell portal="provider" title={title}>
      <div className="provider-portal">
        <ProductSubmissionPrototype session={session} />
        {isProducts ? (
          <Products id={suffix.slice('/products/'.length) || undefined} session={session} />
        ) : suffix === '/documents' ? (
          <Documents session={session} />
        ) : suffix === '/submissions' ? (
          <Submissions session={session} />
        ) : suffix === '/organization' ? (
          <Organization />
        ) : suffix === '' ? (
          <Home />
        ) : (
          <section className="portal-card provider-empty">
            <h1>页面不存在</h1>
            <a href={`${PROVIDER_BASE}?synLive=0`}>返回工作台</a>
          </section>
        )}
      </div>
    </PortalShell>
  );
}
